import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  Chip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  ShoppingCart as OrdersIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Science as MaterialsIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Store as StoreIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';

const drawerWidth = 280;

const InternalNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { org, clearOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get feature flags - Enable EPL Purchase Orders
  const allowEplPOs = true; // Enable EPL purchase orders as requested
  
  const currentCompany = org; // Use org from context

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    clearOrg(); // Clear org on logout
    onLogout();
  };
  
  const handleSwitchOrg = () => {
    clearOrg();
    navigate('/org-select');
  };

  // Build menu items based on org and feature flags - ordered as requested
  const menuItems = [
    { text: 'Dashboard', icon: <SpaceDashboardIcon />, path: `/internal/${currentCompany}/dashboard` },
    { text: 'Order Processing', icon: <OrdersIcon />, path: `/internal/${currentCompany}/order-processing` },
    { text: 'Receive Inventory', icon: <InventoryIcon />, path: `/internal/${currentCompany}/receive-inventory` },
    { text: 'Inventory Management', icon: <WarehouseIcon />, path: `/internal/${currentCompany}/inventory-management` },
    // { text: 'Raw Materials', icon: <MaterialsIcon />, path: `/internal/${currentCompany}/raw-materials` },
    { text: 'Client Management', icon: <GroupIcon />, path: `/internal/${currentCompany}/client-management` },
    { text: 'Client Analytics', icon: <AnalyticsIcon />, path: `/internal/${currentCompany}/client-analytics` },
    // Show Purchase Orders based on org and feature flags
    ...(currentCompany === 'bare' || (currentCompany === 'epl' && allowEplPOs) 
      ? [{ text: 'Purchase Orders', icon: <StoreIcon />, path: `/internal/${currentCompany}/purchase-orders` }] 
      : []),
    { text: 'System Settings', icon: <SettingsIcon />, path: `/internal/${currentCompany}/settings` }
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Unified Header Section */}
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        color: 'white'
      }}>
        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AdminIcon sx={{ mr: 2, fontSize: 32, color: 'white' }} />
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: 'white' }}>
              Internal — {currentCompany === 'bare' ? 'BARE' : 'EPL'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)' }}>
              {currentCompany === 'bare' ? 'Bare Biologics Operations' : 'EPL Operations'}
            </Typography>
          </Box>
        </Box>

        {/* User Info in Same Block */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ mr: 2, bgcolor: 'rgba(255, 255, 255, 0.15)', color: 'white', width: 40, height: 40, border: '2px solid rgba(255, 255, 255, 0.3)', fontWeight: 600 }}>
            {(currentUser?.name || 'Admin').split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white' }}>
              {currentUser?.name || 'Demo Internal User'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
              Operations Manager
            </Typography>
          </Box>
        </Box>

        {/* Access Badges */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip 
            label="Admin Access" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.15)', 
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontWeight: 600,
              fontSize: '0.7rem'
            }} 
          />
          <Chip 
            label="Live System" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.15)', 
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontWeight: 600,
              fontSize: '0.7rem'
            }} 
          />
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ px: 2, py: 1, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: 2,
                backgroundColor: location.pathname === item.path 
                  ? '#e3e8e3' 
                  : item.highlight 
                    ? 'rgba(92, 114, 92, 0.08)' 
                    : 'transparent',
                color: location.pathname === item.path 
                  ? '#5c725c' 
                  : item.highlight 
                    ? '#5c725c' 
                    : '#374151',
                border: item.highlight ? '1px solid rgba(92, 114, 92, 0.2)' : 'none',
                '&:hover': {
                  backgroundColor: location.pathname === item.path 
                    ? '#e3e8e3' 
                    : '#f6f7f6'
                },
                py: 1.5,
                px: 2
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path 
                    ? '#5c725c' 
                    : item.highlight 
                      ? '#5c725c' 
                      : '#6b7280',
                  minWidth: 40 
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.875rem', 
                  fontWeight: location.pathname === item.path || item.highlight ? 600 : 500 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Logout Section */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ 
            mb: 2,
            bgcolor: '#ef4444',
            color: 'white',
            fontWeight: 600,
            '&:hover': { 
              bgcolor: '#dc2626',
              transform: 'translateY(-1px)'
            }
          }}
        >
          Sign Out
        </Button>
        {(
          currentUser?.isSuperAdmin === true ||
          (typeof currentUser?.organization === 'string' && currentUser.organization.toUpperCase() === 'BOTH')
        ) && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={handleSwitchOrg}
            sx={{ 
              mb: 2,
              borderColor: '#039582',
              color: '#039582',
              '&:hover': { 
                borderColor: '#027A6B',
                bgcolor: '#f0fdfa'
              }
            }}
          >
            Switch Organization
          </Button>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: 1, borderColor: '#e2e8f0' }}>
        <Typography variant="body2" align="center" sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
          © 2024 UnisynHub
        </Typography>
        <Typography variant="body2" align="center" sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
          {currentCompany === 'bare' ? 'Bare Biologics' : 'EPL'} Operations
        </Typography>
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              position: 'fixed',
              height: '100vh',
              top: 0,
              left: 0,
              zIndex: 1200,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ 
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1300,
          display: { md: 'none' },
          bgcolor: '#039582',
          color: 'white',
          '&:hover': { bgcolor: '#027A6B' }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Main content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          overflowY: 'auto'
        }}
      >
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default InternalNavigation; 